/**
 * Modern web sign-up flow.
 * ─────────────────────────────────────────────────────────────────────────
 * Three stages, all rendered in the same `BasicLayout` + ParticlesBackground
 * shell as the sign-in page so the visual transition is seamless:
 *
 *   1. **Profile** — same fields as the mobile `RegisterScreen.tsx`
 *      (first / last name, email, password, occupation, year of study,
 *      university, country). Submitting requests an OTP via
 *      `requestSignUpOtp` and advances the stepper.
 *   2. **OTP** — 6-digit code typed into a single masked field. We call
 *      `verifySignUpOtp` and, on success, immediately POST to
 *      `auth/register` (`RegisterCall`) so the access token lands in
 *      Redux without a separate login round-trip — same pattern as the
 *      mobile app's `RegisterScreen2.tsx`.
 *   3. **Paywall** — the dashboard is annual-only on the web, so we show
 *      `AnnualPaywall` which opens RevenueCat's Stripe-backed checkout.
 *      Monthly billing only exists in the iOS / Android apps (Apple /
 *      Google IAP), and the paywall surfaces store deep links for
 *      students who actually want monthly.
 *
 * This file deliberately replaces the Material-Dashboard demo sign-up
 * (name + email + password, no API wiring) that previously lived here —
 * see `git log` for the original boilerplate.
 *
 * Why three stages on one route instead of three routes?
 *   - Each stage carries non-trivial in-memory state (form values, the
 *     freshly-issued user record, Stripe checkout result). Splitting
 *     them across routes would force us to either persist that state
 *     in Redux (overkill) or in URL params (leaks PII into history).
 *     A single component with `step` state keeps the trust boundary
 *     tight.
 *   - The user can refresh the page to start over from step 1 — the
 *     account-creation network call only fires *after* OTP verification,
 *     so a refresh mid-flow won't strand half-registered records.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import BasicLayout from "layouts/authentication/components/BasicLayout";
import ParticlesBackground from "layouts/authentication/components/ParticlesBackground";

import countries from "../../../data/countries.json";
import {
  RegisterCall,
  requestSignUpOtp,
  verifySignUpOtp,
} from "../../../redux/api/auth";

import AnnualPaywall from "./components/AnnualPaywall";
import { consumeGoogleSignupPrefill } from "./googleSignupPrefill";

const STEPS = ["Account", "Verify email", "Annual subscription"];

/*
  When the user arrives here from a Google sign-in 404, OTP is
  redundant — Google has already verified the email and we have a
  trusted `social_auth_id` (the Google `sub`). The two-step variant
  collapses to "fill profile → paywall" since the BE accepts a
  social-auth registration without an OTP round-trip (same as the
  mobile flow in `AppleRegisterProfileScreen.tsx`).
*/
const STEPS_GOOGLE = ["Profile", "Annual subscription"];

const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));

/** Year of Study options (1-10). Mirrors `microlab/src/screens/landing/RegisterScreen.tsx`. */
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, idx) => `${idx + 1}`);

/*
  Generate an opaque random password for social-auth registrations.
  The user never sees or types this — they'll always sign in via
  Google going forward, but the BE's `auth/register` endpoint requires
  a non-empty password column. Mirrors
  `microlab/src/utils/randomPassword.ts` byte-for-byte so a single
  user has the same kind of placeholder regardless of which platform
  they registered on.
*/
const randomOpaquePassword = (length = 32) => {
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  const out = [];
  const arr = new Uint8Array(length);
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < length; i += 1) {
      arr[i] = Math.floor(Math.random() * 256);
    }
  }
  for (let i = 0; i < length; i += 1) {
    out.push(chars[arr[i] % chars.length]);
  }
  return out.join("");
};

function Cover() {
  /* ────────────────────── stepper state ────────────────────── */
  const [step, setStep] = useState(0);

  /*
    Picked up once on mount from sessionStorage. When non-null the
    user landed here by clicking "Sign in with Google" → BE returned
    404 (= no Microlab account yet). In that case:
      • email + first/last name are pre-filled from the verified
        Google profile and locked (the user can't change them — the
        BE will key the account off the Google `sub`).
      • the password field disappears entirely; we generate an opaque
        placeholder behind the scenes since the BE schema requires a
        non-empty password column.
      • the OTP step is skipped — Google already verified the email.
  */
  const [googlePrefill] = useState(() => consumeGoogleSignupPrefill());
  const isGoogleSignup = Boolean(googlePrefill);

  /* ────────────────────── profile state ────────────────────── */
  const [firstName, setFirstName] = useState(googlePrefill?.firstName || "");
  const [lastName, setLastName] = useState(googlePrefill?.lastName || "");
  const [email, setEmail] = useState(googlePrefill?.email || "");
  const [password, setPassword] = useState("");
  const [occupation, setOccupation] = useState("Student");
  const [occupationOther, setOccupationOther] = useState("");
  const [yearOfStudy, setYearOfStudy] = useState("");
  const [university, setUniversity] = useState("");
  const [country, setCountry] = useState(null);

  /* ────────────────────── otp + auth state ────────────────────── */
  const [otp, setOtp] = useState("");
  const [registeredUser, setRegisteredUser] = useState(null);

  /* ────────────────────── transient UX state ────────────────────── */
  const [busy, setBusy] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (msg) => {
    setErrorMessage(msg);
    setErrorOpen(true);
  };

  const isProfileValid = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!isEmailValid(email)) return false;
    // Password isn't required on the Google path (we generate one for
    // the BE's schema constraint when we POST `auth/register`).
    if (!isGoogleSignup && password.length < 8) return false;
    if (!occupation) return false;
    if (occupation === "Other" && !occupationOther.trim()) return false;
    if (!yearOfStudy) return false;
    if (!country?.value) return false;
    return true;
  }, [
    firstName,
    lastName,
    email,
    password,
    occupation,
    occupationOther,
    yearOfStudy,
    country,
    isGoogleSignup,
  ]);

  /*
    Build the body sent to POST /auth/register. Shared between the
    Google fast-path (skip OTP) and the email/OTP slow-path so both
    end up with identical field shapes — the only deltas are the
    `social_auth_*` fields, which only the Google path includes.
  */
  const buildRegisterBody = () => {
    const lang = (typeof window !== "undefined"
      ? window.localStorage.getItem("i18nextLng") || "en"
      : "en"
    )
      .split("-")[0]
      .toLowerCase();

    const occupationForApi =
      occupation === "Other"
        ? occupationOther.trim() || "Other"
        : occupation;

    const body = {
      email: email.trim().toLowerCase(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      // Use the user-typed password on the email path, an opaque
      // random one on the Google path so the BE's NOT-NULL schema
      // constraint is satisfied without forcing the user to invent
      // a password they'll never use.
      password: isGoogleSignup ? randomOpaquePassword() : password,
      platform: "web",
      app_type: "microlab",
      year_study: yearOfStudy,
      occupation: occupationForApi,
      university: university.trim(),
      country: country?.value || "",
      lang,
    };
    if (isGoogleSignup && googlePrefill) {
      body.social_auth_provider = "google";
      body.social_auth_id = googlePrefill.socialAuthId;
    }
    return body;
  };

  /* ────────────────────── step 1 → 2 (or 1 → paywall on Google) ────────────────────── */
  const onSubmitProfile = async (e) => {
    e?.preventDefault?.();
    if (!isProfileValid || busy) return;
    setBusy(true);
    try {
      // ── Google path: BE registration straight away (no OTP). ──
      if (isGoogleSignup) {
        const body = buildRegisterBody();
        const response = await RegisterCall(body);
        if (response?.access_token) {
          setRegisteredUser({ id: response.id, email: body.email });
          setStep(2);
          return;
        }
        if (response?.status === 409) {
          showError(
            "An account already exists for this email. Please sign in instead."
          );
          return;
        }
        showError(
          response?.message ||
            "We couldn't create the account. Please try again."
        );
        return;
      }

      // ── Email/password path: send OTP, advance to step 1. ──
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const result = await requestSignUpOtp(email, fullName);
      if (!result.ok) {
        if (result.response?.status === 409) {
          showError(
            "An account already exists for this email. Try signing in instead."
          );
        } else {
          showError(
            result.response?.message ||
              "We couldn't send the verification email. Please try again."
          );
        }
        return;
      }
      setStep(1);
    } catch (err) {
      showError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  /* ────────────────────── step 2 → 3 ────────────────────── */
  const submitOtp = async (codeArg) => {
    const code = (codeArg ?? otp).replace(/\D/g, "");
    if (code.length !== 6 || busy) return;
    setBusy(true);
    try {
      const verified = await verifySignUpOtp(email, code);
      if (!verified) {
        showError(
          "That code didn't match. Double-check the email we sent and try again."
        );
        return;
      }

      const body = buildRegisterBody();
      const response = await RegisterCall(body);
      if (response?.access_token) {
        setRegisteredUser({
          id: response.id,
          email: body.email,
        });
        setStep(2);
        return;
      }
      if (response?.status === 409) {
        showError(
          "An account already exists for this email. Try signing in instead."
        );
        return;
      }
      showError(
        response?.message || "We couldn't create the account. Please try again."
      );
    } catch (err) {
      showError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  /*
    Auto-submit when the user types the 6th digit. Mirrors the mobile
    app's `RegisterScreen2.tsx` UX so a returning user gets the same
    "tap, tap, tap, done" feel on either platform.
  */
  useEffect(() => {
    if (step !== 1) return undefined;
    const digits = otp.replace(/\D/g, "");
    if (digits.length !== 6) return undefined;
    const t = setTimeout(() => {
      void submitOtp(digits);
    }, 200);
    return () => clearTimeout(t);
  }, [otp, step]);

  /* ────────────────────── step 3 ────────────────────── */
  const onPaywallSuccess = () => {
    /*
      Reload (instead of `navigate('/dashboard')`) so the freshly-issued
      access token + RC entitlement are picked up by every layout-level
      bootstrap effect (subscription gate, role-based redirect, etc.).
      This is the same pattern the existing sign-in page uses.
    */
    if (typeof window !== "undefined") window.location.replace("/dashboard");
  };

  /* ────────────────────── render ────────────────────── */
  /*
    The Google fast-path collapses the stepper from three indices
    (0=profile, 1=otp, 2=paywall) to two (0=profile, 1=paywall) —
    so we map the live `step` onto the displayed step here without
    rewiring the rest of the state machine. Step `2` (paywall) is
    rendered as the second dot in `STEPS_GOOGLE` (index 1).
  */
  const visibleSteps = isGoogleSignup ? STEPS_GOOGLE : STEPS;
  const visibleStep = isGoogleSignup ? (step === 2 ? 1 : 0) : step;

  return (
    <BasicLayout animation={<ParticlesBackground />}>
      {step !== 2 ? (
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              {step === 0
                ? isGoogleSignup
                  ? "Finish your profile"
                  : "Create your account"
                : "Verify your email"}
            </MDTypography>
            {/*
              Subtitle is rendered only when there's something useful
              to say. Plain email sign-up at step 0 has no subtitle —
              the previous "Same fields as the app — annual web
              subscription…" copy was misleading (it implied web is
              annual-only at the registration step, which is enforced
              later at the paywall, not here) and didn't add helpful
              guidance, so we drop it. Google prefill still surfaces
              the pre-filled email, and the OTP step still tells the
              user where the code was sent.
            */}
            {(step !== 0 || isGoogleSignup) && (
              <MDTypography display="block" variant="button" color="white" my={1}>
                {step === 0
                  ? `Continuing as ${googlePrefill?.email}. A few details and you're in.`
                  : `We sent a 6-digit code to ${email}. Enter it below.`}
              </MDTypography>
            )}
          </MDBox>

          <MDBox px={3} pt={2}>
            <Stepper activeStep={visibleStep} alternativeLabel>
              {visibleSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </MDBox>

          {step === 0 ? (
            <MDBox component="form" role="form" pt={2} pb={3} px={3} onSubmit={onSubmitProfile}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
                <MDInput
                  type="text"
                  label="First name"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isGoogleSignup}
                />
                <MDInput
                  type="text"
                  label="Last name"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isGoogleSignup}
                />
              </Stack>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isGoogleSignup}
                />
              </MDBox>
              {/*
                Hide the password field on the Google path. The BE
                still needs SOMETHING in the column (NOT NULL), but
                we generate an opaque random one in `buildRegisterBody`
                so the user is never bothered for a password they'll
                never use.
              */}
              {!isGoogleSignup ? (
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Password (min 8 characters)"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </MDBox>
              ) : null}

              {/* Occupation toggle — Student vs Other-with-text — matches
                  the mobile signup. The BE persists this in `occupation`. */}
              <MDBox mb={2}>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Occupation
                </MDTypography>
                <ToggleButtonGroup
                  exclusive
                  value={occupation}
                  onChange={(_, val) => val && setOccupation(val)}
                  fullWidth
                  sx={{ mt: 0.5 }}
                >
                  <ToggleButton value="Student">Student</ToggleButton>
                  <ToggleButton value="Other">Other</ToggleButton>
                </ToggleButtonGroup>
              </MDBox>
              {occupation === "Other" ? (
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="What do you do?"
                    fullWidth
                    value={occupationOther}
                    onChange={(e) => setOccupationOther(e.target.value)}
                  />
                </MDBox>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
                <TextField
                  select
                  label="Year of study"
                  variant="standard"
                  fullWidth
                  value={yearOfStudy}
                  onChange={(e) => setYearOfStudy(e.target.value)}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </TextField>
                <MDInput
                  type="text"
                  label="University (optional)"
                  fullWidth
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                />
              </Stack>

              <MDBox mb={2}>
                <Autocomplete
                  options={countries}
                  value={country}
                  onChange={(_, value) => setCountry(value)}
                  getOptionLabel={(opt) => opt?.label || ""}
                  isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Country" variant="standard" fullWidth />
                  )}
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={!isProfileValid || busy}
                >
                  {busy
                    ? isGoogleSignup
                      ? "Creating account…"
                      : "Sending code…"
                    : isGoogleSignup
                    ? "Continue to subscription"
                    : "Send verification code"}
                </MDButton>
              </MDBox>

              <MDBox mt={2} textAlign="center">
                <MDTypography variant="button" color="text">
                  Already have an account?{" "}
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign in
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox component="form" role="form" pt={2} pb={3} px={3}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  inputMode="numeric"
                  label="6-digit code"
                  fullWidth
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                />
              </MDBox>

              <MDBox mt={3} mb={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={otp.replace(/\D/g, "").length !== 6 || busy}
                  onClick={() => submitOtp()}
                >
                  {busy ? "Verifying…" : "Verify & continue"}
                </MDButton>
              </MDBox>
              <MDBox mt={1.5} display="flex" justifyContent="space-between">
                <MDButton
                  variant="text"
                  color="info"
                  onClick={() => {
                    setOtp("");
                    setStep(0);
                  }}
                >
                  Edit details
                </MDButton>
                <MDButton
                  variant="text"
                  color="info"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
                      const result = await requestSignUpOtp(email, fullName);
                      if (!result.ok) {
                        showError(
                          result.response?.message ||
                            "Couldn't resend the code. Try again in a moment."
                        );
                      }
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Resend code
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </Card>
      ) : (
        <AnnualPaywall
          appUserId={registeredUser?.id}
          email={registeredUser?.email}
          onSuccess={onPaywallSuccess}
        />
      )}

      <MDSnackbar
        color="error"
        icon="warning"
        title={errorMessage}
        dateTime=""
        content=""
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        close={() => setErrorOpen(false)}
        bgWhite
      />
    </BasicLayout>
  );
}

export default Cover;
